import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreatePasswordlessUserRequest,
  UpdateUserRequest,
  User,
} from '@interfaces/users.interface';

import {
  useCreatePasswordlessUserMutation,
  useUpdateUserMutation,
} from '@api/users.api';

import useFormEnhanced from '@hooks/useFormEnhanced';

export const UPSERT_USER_FORM_MODAL_ID = 'upsertUser';

interface UpsertUserFormProps {
  user?: User;
}

export default function UpsertUserForm({ user }: UpsertUserFormProps) {
  const initialValues = {
    name: user?.name || '',
    email: user?.email || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['name'],
      lowercaseFields: ['email'],
    },
  });

  const [createUser, { isLoading: isLoadingCreate }] =
    useCreatePasswordlessUserMutation();
  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_USER_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      if (user) {
        const data: UpdateUserRequest = {
          id: user.id,
          body: {
            name: values.name,
            email: values.email,
          },
        };

        await updateUser(data).unwrap();

        notifications.show({
          title: 'Utente modificato',
          message: 'Utente modificato con successo.',
        });
      } else {
        const data: CreatePasswordlessUserRequest = {
          name: values.name,
          email: values.email,
        };

        await createUser(data).unwrap();

        notifications.show({
          title: 'Utente creato',
          message:
            "Nuovo utente creato con successo. L'utente riceverà un'email con le istruzioni per accedere.",
        });
      }

      closeModal(UPSERT_USER_FORM_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Nome"
          {...form.getInputProps('name')}
        />
        <TextInput
          required
          type="email"
          label="Email"
          description="Verrà inviata un'email con le istruzioni per accedere"
          {...form.getInputProps('email')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
